import type { StationeryProduct } from '@shared/utils/print-price/types';
import { getOrientation } from '@apps/card/utils';
import { isPostcard } from '@apps/card/utils/cardSizeName';
import type { BuildProductSizeOptionsArgs, ProductSizeOption, ProductSizeTemplate } from './productSizeOptions.types';

const isOption = (input: ProductSizeOption | null): input is ProductSizeOption => !!input;

// Digital products have no physical size, so a digital row carries no
// measurements and its product name is the whole label.
const isDigitalOption = (option: ProductSizeOption): boolean => !option.product.cardTypeSKU;

const PREMIUM_DIGITAL_PRODUCT_TYPE = 'digitalCard';

/**
 * The rows of the PDP's combined product+size picker, in display order: classic
 * paper sizes, postcard, magnet, then the digital variants together at the
 * bottom (Premium above Basic). One fixed order on every category, so the
 * picker never reshuffles as a couple moves between PDPs; within a block,
 * sizes stay ascending.
 */
export const buildProductSizeOptions = ({ templateCategory, currentTemplate, currentProducts, themeTemplates, mapProduct }: BuildProductSizeOptionsArgs): ProductSizeOption[] => {
  const toOption = (template: { id: string; widthInches: number; heightInches: number }, product: StationeryProduct): ProductSizeOption | null => {
    const mapped = mapProduct(product);
    if (!mapped) return null;
    return {
      key: `${template.id}:${product.id}`,
      templateId: template.id,
      product: mapped,
      widthInches: template.widthInches,
      heightInches: template.heightInches,
    };
  };
  const toDigitalOption = (templateId: string, product: StationeryProduct): ProductSizeOption | null => toOption({ id: templateId, widthInches: 0, heightInches: 0 }, product);

  // Digital rows on the current template come from the PDP's product list
  // rather than the theme query because the PDP already applied its visibility
  // rules there (free-digital dummy, basicDigitalV2 kill switch).
  const ownDigitalOptions = currentProducts
    .map(p => toDigitalOption(currentTemplate?.id ?? '', p))
    .filter(isOption)
    .filter(isDigitalOption);

  const orderDigital = (options: ProductSizeOption[]): ProductSizeOption[] => [
    ...options.filter(o => o.product.productType === PREMIUM_DIGITAL_PRODUCT_TYPE),
    ...options.filter(o => o.product.productType !== PREMIUM_DIGITAL_PRODUCT_TYPE),
  ];

  if (!currentTemplate) return orderDigital(ownDigitalOptions);

  const orientation = getOrientation(currentTemplate.widthInches, currentTemplate.heightInches);
  const sizeTemplates: ProductSizeTemplate[] = themeTemplates
    // Orientation has its own picker; the size axis stays within the current one.
    .filter(t => orientation === getOrientation(t.widthInches, t.heightInches));
  // The current template can be missing from the enabled-only list (e.g. a
  // direct link to a disabled template) — fall back to its own products so
  // the control still renders the selection.
  if (!sizeTemplates.some(t => t.id === currentTemplate.id)) {
    sizeTemplates.push({ ...currentTemplate, products: currentProducts });
  }
  sizeTemplates.sort((a, b) => a.widthInches - b.widthInches || a.heightInches - b.heightInches);

  const sizedOptions = sizeTemplates.flatMap(t =>
    t.products
      .map(p => toOption(t, p))
      .filter(isOption)
      .filter(o => !isDigitalOption(o)),
  );

  // Premium Digital is sold per theme, not per size: card_service links it to
  // the theme's classic 5x7 template but not to its postcard/magnet 4x6
  // sibling. Pinning digital rows to the current template made the row vanish
  // the moment a couple picked "Postcard 4x6" and reappear on "Classic Paper
  // 5x7". When the current template has no Premium Digital of its own, borrow
  // it from the nearest sibling that has one (same orientation first, sizes
  // ascending, then any other template of the theme): choosing that row
  // navigates the PDP to the sibling with `?format=premiumDigital`, exactly
  // like a size change does. Only Premium Digital is borrowed — the free
  // digital rows are subject to the PDP's own visibility rules, which the raw
  // sibling products have not been through.
  //
  // Never from a postcard: card_service links Premium Digital to postcard
  // templates only so a postcard order can be reordered as a matching digital
  // card, and the PDP does not sell it (it drops the current postcard's too).
  const hasOwnPremiumDigital = ownDigitalOptions.some(o => o.product.productType === PREMIUM_DIGITAL_PRODUCT_TYPE);
  const borrowedPremiumDigital = hasOwnPremiumDigital
    ? []
    : [...sizeTemplates.filter(t => t.id !== currentTemplate.id), ...themeTemplates.filter(t => !sizeTemplates.includes(t))]
        .filter(t => !isPostcard(templateCategory, t.widthInches, t.heightInches))
        .flatMap(t => t.products.filter(p => p.productType === PREMIUM_DIGITAL_PRODUCT_TYPE).map(p => toDigitalOption(t.id, p)))
        .filter(isOption)
        .slice(0, 1);

  const digitalOptions = orderDigital([...borrowedPremiumDigital, ...ownDigitalOptions]);

  const magnetOptions = sizedOptions.filter(o => o.product.productType === 'magnet');
  const classicPaperOptions = sizedOptions.filter(o => o.product.productType !== 'magnet' && !isPostcard(templateCategory, o.widthInches, o.heightInches));
  const postcardOptions = sizedOptions.filter(o => o.product.productType !== 'magnet' && isPostcard(templateCategory, o.widthInches, o.heightInches));
  return [...classicPaperOptions, ...postcardOptions, ...magnetOptions, ...digitalOptions];
};
